import { useState } from 'react'
import { Copy, Check, Globe, Package, Zap } from 'lucide-react'

const IntegrationGuide = ({ widget }) => {
  const [copiedCode, setCopiedCode] = useState('')
  const [activeMethod, setActiveMethod] = useState('cdn')

  const integrationMethods = [
    {
      id: 'cdn',
      name: 'CDN (Recommended)',
      icon: Globe,
      description: 'Load the widget directly from our CDN',
      pros: ['Fast loading', 'Always up-to-date', 'No hosting required'],
      code: `<script 
  src="https://cdn.talkida.co.in/talkida-widget.js" 
  bot-id="${widget.botId}"
  bot-token="${widget.botToken}"
  data-position="${widget.position}"
  data-theme="${widget.theme}"
  data-primary-color="${widget.primaryColor}"
  data-welcome-message="${widget.greetingMsg || widget.welcomeMessage || 'Hello! How can I help you today?'}"
  data-placeholder="${widget.placeholder}"
  data-show-welcome="false"
  data-auto-open="${widget.autoOpen}"
  data-z-index="${widget.zIndex}"
  data-chatbot-avatar="${widget.chatbotAvatar || '🤖'}"
  data-custom-avatar="${widget.customAvatar || ''}"
  data-bot-name="${widget.botName || 'Talkida Technologies'}"
  defer 
  type="text/javascript">
</script>`
    }
  ]

  const copyToClipboard = async (code, methodId) => {
    try {
      await navigator.clipboard.writeText(code)
      setCopiedCode(methodId)
      setTimeout(() => setCopiedCode(''), 2000)
    } catch (err) {
      console.error('Failed to copy: ', err)
    }
  }

  const currentMethod = integrationMethods.find(method => method.id === activeMethod)

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-white mb-2">Integration Methods</h3>
        <p className="text-secondary-400">
          Choose the integration method that best fits your project
        </p>
      </div>

      {/* Method Selection */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {integrationMethods.map((method) => {
          const Icon = method.icon
          const isActive = activeMethod === method.id
          const isDisabled = method.id === 'npm'

          return (
            <button
              key={method.id}
              onClick={() => !isDisabled && setActiveMethod(method.id)}
              disabled={isDisabled}
              className={`p-4 rounded-lg border transition-all text-left ${isActive
                ? 'border-primary-500 bg-primary-500/20'
                : isDisabled
                  ? 'border-secondary-600 bg-secondary-800/50 opacity-50 cursor-not-allowed'
                  : 'border-secondary-600 bg-secondary-800 hover:border-secondary-500'
                }`}
            >
              <div className="flex items-center space-x-3 mb-2">
                <Icon className="w-5 h-5 text-primary-400" />
                <span className="font-medium text-white">{method.name}</span>
                {isDisabled && (
                  <span className="text-xs bg-yellow-500/20 text-yellow-400 px-2 py-1 rounded">
                    Coming Soon
                  </span>
                )}
              </div>
              <p className="text-sm text-secondary-400 mb-3">{method.description}</p>
              <div className="space-y-1">
                {method.pros.map((pro, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <div className="w-1 h-1 bg-primary-400 rounded-full"></div>
                    <span className="text-xs text-secondary-300">{pro}</span>
                  </div>
                ))}
              </div>
            </button>
          )
        })}
      </div>

      {/* Code Display */}
      {currentMethod && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-md font-medium text-white">
              {currentMethod.name} Integration Code
            </h4>
            <button
              onClick={() => copyToClipboard(currentMethod.code, activeMethod)}
              className="flex items-center space-x-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors"
            >
              {copiedCode === activeMethod ? (
                <>
                  <Check className="w-4 h-4" />
                  <span>Copied!</span>
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  <span>Copy Code</span>
                </>
              )}
            </button>
          </div>

          <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
            <pre className="text-sm text-gray-100">
              <code>{currentMethod.code}</code>
            </pre>
          </div>
        </div>
      )}

      {/* Integration Steps */}
      <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
        <h4 className="font-medium text-blue-400 mb-3 flex items-center space-x-2">
          <Zap className="w-4 h-4" />
          <span>Quick Setup Steps</span>
        </h4>
        <ol className="text-sm text-blue-300 space-y-2 list-decimal list-inside">
          <li>Copy the integration code above</li>
          <li>Paste it before the closing <code>&lt;/body&gt;</code> tag of your website</li>
          <li>Replace the API key and chatbot ID with your actual values</li>
          <li>Customize the appearance using the data attributes</li>
          <li>Test the widget on your website</li>
        </ol>
      </div>


      {/* Troubleshooting */}
      <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-4">
        <h4 className="font-medium text-yellow-400 mb-3">Troubleshooting</h4>
        <div className="space-y-2 text-sm text-yellow-300">
          <div>
            <strong>Widget not appearing?</strong> Check that the script is loaded and your API key is correct.
          </div>
          <div>
            <strong>Styling issues?</strong> Make sure there are no CSS conflicts with your existing styles.
          </div>
          <div>
            <strong>Not receiving messages?</strong> Verify your chatbot ID and check your API endpoints.
          </div>
        </div>
      </div>
    </div>
  )
}

export default IntegrationGuide
